import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ROOMS } from "@/lib/constants";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Package,
  Plus,
  Trash2,
  Home,
  Warehouse,
  Truck,
  Edit2,
  ZoomIn,
  X,
  Camera,
  Upload,
  Download,
  QrCode,
  Shield,
  AlertCircle,
  Mail,
  Calendar,
} from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { format } from "date-fns";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { CameraCapture } from "@/components/CameraCapture";
import { PickupDetailsCard } from "@/components/PickupDetailsCard";
import { ShippingToStorageCard } from "@/components/ShippingToStorageCard";
import { InStorageCard } from "@/components/InStorageCard";
import { RequestReturnCard } from "@/components/RequestReturnCard";
import { ReturnShipmentCard } from "@/components/ReturnShipmentCard";

interface Box {
  id: string;
  box_number: string;
  name: string;
  description: string | null;
  room: string;
  location_status: string;
  created_at: string;
  has_exterior_photo: boolean;
  shipping_label_url: string | null;
  shipping_label_qr_url: string | null;
  tracking_number: string | null;
  label_generated_at: string | null;
  insurance_opted_out: boolean | null;
  return_requested_at: string | null;
  storage_start_date: string | null;
  stored_at: string | null;
}

interface Item {
  id: string;
  name: string;
  quantity: number;
  room: string;
  notes: string | null;
  ai_generated: boolean;
}

interface Photo {
  id: string;
  storage_path: string;
  thumbnail_path: string | null;
  uploaded_at: string;
  photo_type: string | null;
  ai_classification_confidence: number | null;
}

interface Shipment {
  id: string;
  pickup_date: string;
  pickup_time_slot: string;
  status: string;
  type: string;
  box_count: number;
  direction?: string;
  tracking_number?: string;
  label_url?: string;
  delivery_date?: string;
}

const BoxDetailConnected = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [box, setBox] = useState<Box | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [shipment, setShipment] = useState<Shipment | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAddItem, setShowAddItem] = useState(false);
  const [showEditBox, setShowEditBox] = useState(false);
  const [showEditItem, setShowEditItem] = useState(false);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [lightboxPhoto, setLightboxPhoto] = useState<string | null>(null);
  const [showCamera, setShowCamera] = useState(false);
  const [showPhotoOptions, setShowPhotoOptions] = useState(false);
  const [photoType, setPhotoType] = useState<'exterior' | 'interior'>('interior');
  const [signedPhotoUrls, setSignedPhotoUrls] = useState<Record<string, string>>({});
  const [emailingLabel, setEmailingLabel] = useState(false);
  const [newItem, setNewItem] = useState({
    name: "",
    quantity: 1,
    room: "Other",
    notes: "",
  });
  const [editedBox, setEditedBox] = useState({
    name: box?.name || "",
    description: box?.description || "",
    room: box?.room || "Other",
    box_number: box?.box_number || "",
  });
  const [editedItem, setEditedItem] = useState({
    name: "",
    quantity: 1,
    room: "Other",
    notes: "",
  });

  useEffect(() => {
    fetchBoxData();
  }, [id]);

  const fetchSignedPhotoUrls = async (boxId: string) => {
    const { data: photoData } = await supabase
      .from('photos')
      .select('id, storage_path')
      .eq('box_id', boxId);

    if (photoData) {
      const urlPromises = photoData.map(async (photo) => {
        const { data, error } = await supabase.storage
          .from('box-photos')
          .createSignedUrl(photo.storage_path, 3600); // 1 hour expiration
        
        return { ...photo, signedUrl: error ? '' : data.signedUrl };
      });

      return await Promise.all(urlPromises);
    }
    return [];
  };

  const fetchBoxData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/auth');
        return;
      }

      // Fetch box
      const { data: boxData, error: boxError } = await supabase
        .from('boxes')
        .select('*')
        .eq('id', id)
        .eq('user_id', user.id)
        .maybeSingle();

      if (boxError) throw boxError;
      
      if (!boxData) {
        toast({
          title: "Box Not Found",
          description: "This box doesn't exist or you don't have access to it.",
          variant: "destructive",
        });
        navigate('/dashboard');
        return;
      }

      setBox(boxData);
      setEditedBox({
        name: boxData.name,
        description: boxData.description || "",
        room: boxData.room,
        box_number: boxData.box_number,
      });

      // Fetch items
      const { data: itemsData, error: itemsError } = await supabase
        .from('items')
        .select('*')
        .eq('box_id', id)
        .order('created_at', { ascending: true });

      if (itemsError) throw itemsError;
      setItems(itemsData || []);

      // Fetch photos
      const { data: photosData, error: photosError } = await supabase
        .from('photos')
        .select('*')
        .eq('box_id', id)
        .order('uploaded_at', { ascending: false });

      if (photosError) throw photosError;
      setPhotos(photosData || []);

      // Fetch signed URLs for all photos
      if (photosData && photosData.length > 0) {
        const urlsMap: Record<string, string> = {};
        for (const photo of photosData) {
          const { data: signedUrlData } = await supabase.storage
            .from('box-photos')
            .createSignedUrl(photo.storage_path, 3600);
          
          if (signedUrlData?.signedUrl) {
            urlsMap[photo.id] = signedUrlData.signedUrl;
          }
        }
        setSignedPhotoUrls(urlsMap);
      }

      // Fetch shipment info if box has a label
      if (boxData.label_generated_at) {
        const { data: shipmentData } = await supabase
          .from('shipments')
          .select('id, pickup_date, pickup_time_slot, status, type, box_count, direction, tracking_number, label_url, delivery_date')
          .contains('box_ids', [id])
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        
        if (shipmentData) {
          setShipment(shipmentData);
        }
      }
    } catch (error) {
      console.error('Error fetching box:', error);
      toast({
        title: "Error",
        description: "Failed to load box details",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBox = async () => {
    if (!box || !confirm("Are you sure you want to delete this box?")) return;

    try {
      const { error } = await supabase
        .from('boxes')
        .delete()
        .eq('id', box.id);

      if (error) throw error;

      toast({
        title: "Box Deleted",
        description: `${box.name} has been removed.`,
      });
      navigate('/dashboard');
    } catch (error) {
      console.error('Error deleting box:', error);
      toast({
        title: "Error",
        description: "Failed to delete box",
        variant: "destructive",
      });
    }
  };

  const handleAddItem = async () => {
    if (!box || !newItem.name.trim()) {
      toast({
        title: "Name Required",
        description: "Please enter an item name.",
        variant: "destructive",
      });
      return;
    }

    try {
      const { data, error } = await supabase
        .from('items')
        .insert({
          box_id: box.id,
          name: newItem.name.trim(),
          quantity: newItem.quantity,
          room: newItem.room,
          notes: newItem.notes.trim() || null,
          ai_generated: false,
        })
        .select()
        .single();

      if (error) throw error;

      setItems([...items, data]);
      setNewItem({ name: "", quantity: 1, room: "Other", notes: "" });
      setShowAddItem(false);
      toast({
        title: "Item Added",
        description: `${newItem.name} added to ${box.name}.`,
      });
    } catch (error) {
      console.error('Error adding item:', error);
      toast({
        title: "Error",
        description: "Failed to add item",
        variant: "destructive",
      });
    }
  };

  const handleEditBox = async () => {
    if (!box || !editedBox.name.trim()) {
      toast({
        title: "Name Required",
        description: "Please enter a box name.",
        variant: "destructive",
      });
      return;
    }

    if (!editedBox.box_number.trim()) {
      toast({
        title: "Box Number Required",
        description: "Please enter a box number.",
        variant: "destructive",
      });
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Check for duplicate box number
      const { data: existingBox } = await supabase
        .from('boxes')
        .select('id')
        .eq('user_id', user.id)
        .eq('box_number', editedBox.box_number.trim())
        .neq('id', box.id)
        .maybeSingle();

      if (existingBox) {
        toast({
          title: "Duplicate Box Number",
          description: "This box number already exists. Please use a different one.",
          variant: "destructive",
        });
        return;
      }

      const { error } = await supabase
        .from('boxes')
        .update({
          name: editedBox.name.trim(),
          description: editedBox.description.trim() || null,
          room: editedBox.room,
          box_number: editedBox.box_number.trim(),
        })
        .eq('id', box.id);

      if (error) throw error;

      setBox({
        ...box,
        name: editedBox.name.trim(),
        description: editedBox.description.trim() || null,
        room: editedBox.room,
        box_number: editedBox.box_number.trim(),
      });

      setShowEditBox(false);
      toast({
        title: "Box Updated",
        description: `${editedBox.name} has been updated.`,
      });
    } catch (error) {
      console.error('Error updating box:', error);
      toast({
        title: "Error",
        description: "Failed to update box",
        variant: "destructive",
      });
    }
  };

  const handleEditItem = (itemId: string) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;

    setEditingItemId(itemId);
    setEditedItem({
      name: item.name,
      quantity: item.quantity,
      room: item.room,
      notes: item.notes || "",
    });
    setShowEditItem(true);
  };

  const handleSaveItem = async () => {
    if (!editingItemId || !editedItem.name.trim()) {
      toast({
        title: "Name Required",
        description: "Please enter an item name.",
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('items')
        .update({
          name: editedItem.name.trim(),
          quantity: editedItem.quantity,
          room: editedItem.room,
          notes: editedItem.notes.trim() || null,
        })
        .eq('id', editingItemId);

      if (error) throw error;

      setItems(items.map(item =>
        item.id === editingItemId
          ? {
              ...item,
              name: editedItem.name.trim(),
              quantity: editedItem.quantity,
              room: editedItem.room,
              notes: editedItem.notes.trim() || null,
            }
          : item
      ));

      setShowEditItem(false);
      setEditingItemId(null);
      toast({
        title: "Item Updated",
        description: `${editedItem.name} has been updated.`,
      });
    } catch (error) {
      console.error('Error updating item:', error);
      toast({
        title: "Error",
        description: "Failed to update item",
        variant: "destructive",
      });
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    if (!confirm("Delete this item?")) return;

    try {
      const { error } = await supabase
        .from('items')
        .delete()
        .eq('id', itemId);

      if (error) throw error;

      setItems(items.filter(item => item.id !== itemId));
      toast({
        title: "Item Deleted",
        description: "Item has been removed.",
      });
    } catch (error) {
      console.error('Error deleting item:', error);
      toast({
        title: "Error",
        description: "Failed to delete item",
        variant: "destructive",
      });
    }
  };

  const handleDeletePhoto = async (photoId: string) => {
    if (!confirm("Delete this photo?")) return;

    try {
      const photo = photos.find(p => p.id === photoId);
      if (!photo) return;

      // Delete from storage first
      const { error: storageError } = await supabase.storage
        .from('box-photos')
        .remove([photo.storage_path]);

      if (storageError) {
        console.error('Storage deletion error:', storageError);
      }

      // Delete from database
      const { error } = await supabase
        .from('photos')
        .delete()
        .eq('id', photoId);

      if (error) throw error;

      setPhotos(photos.filter(p => p.id !== photoId));
      toast({
        title: "Photo Deleted",
        description: "Photo has been removed.",
      });
    } catch (error) {
      console.error('Error deleting photo:', error);
      toast({
        title: "Error",
        description: "Failed to delete photo",
        variant: "destructive",
      });
    }
  };

  const getPhotoUrl = (photo: Photo) => {
    return signedPhotoUrls[photo.id] || '';
  };

  const handleCameraCapture = async (imageData: string) => {
    if (!box) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Convert base64 to blob
      const base64Data = imageData.split(',')[1];
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/jpeg' });

      // Upload to storage
      const fileName = `${user.id}/${box.id}/${Date.now()}.jpg`;
      const { error: uploadError } = await supabase.storage
        .from('box-photos')
        .upload(fileName, blob);

      if (uploadError) throw uploadError;

      // Create photo record with the selected photo type
      const { data: newPhoto, error: photoError } = await supabase
        .from('photos')
        .insert({
          box_id: box.id,
          storage_path: fileName,
          photo_type: photoType,
        })
        .select()
        .single();

      if (photoError) throw photoError;

      // Update box exterior photo flag if this is an exterior photo
      if (photoType === 'exterior') {
        await supabase
          .from('boxes')
          .update({ has_exterior_photo: true })
          .eq('id', box.id);
        toast({
          title: "Exterior photo added!",
          description: "Insurance coverage is now active.",
        });
      } else {
        // Trigger AI classification only for interior photos
        toast({
          title: "Analyzing photo...",
          description: "AI is classifying your photo contents.",
        });
        classifyPhoto(newPhoto.id);
      }

      setShowCamera(false);
      fetchBoxData(); // Refresh to get signed URLs
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast({
        title: "Error",
        description: "Failed to upload photo",
        variant: "destructive",
      });
    }
  };

  const classifyPhoto = async (photoId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('classify-box-photo', {
        body: { photo_id: photoId, box_id: id }
      });

      if (error) throw error;

      // Refresh box and photos data
      await fetchBoxData();

      if (data?.classification === 'exterior') {
        toast({
          title: "Exterior Photo Verified!",
          description: "Insurance coverage active ($100/box).",
        });
      } else if (data?.classification === 'interior') {
        toast({
          title: "Interior Photo Detected",
          description: "Please add an exterior photo (sealed box) for insurance coverage.",
        });
      }
    } catch (error) {
      console.error('Error classifying photo:', error);
      // Don't show error to user, just log it
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !box) return;

    try {
      const file = files[0];
      const reader = new FileReader();
      
      reader.onloadend = async () => {
        const base64Image = reader.result as string;
        await handleCameraCapture(base64Image);
        setShowPhotoOptions(false);
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading file:', error);
      toast({
        title: "Error",
        description: "Failed to upload photo",
        variant: "destructive",
      });
    }
  };

  const handleEmailLabel = async () => {
    if (!shipment) return;
    
    setEmailingLabel(true);
    try {
      const { data, error } = await supabase.functions.invoke('email-shipping-labels', {
        body: { shipmentId: shipment.id }
      });

      if (error) throw error;

      toast({
        title: "Label Sent",
        description: "Shipping label has been sent to your email",
      });
    } catch (error: any) {
      console.error('Error emailing label:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to email label. Please try again.",
        variant: "destructive",
      });
    } finally {
      setEmailingLabel(false);
    }
  };


  const getLocationBadge = (status: string) => {
    const variants: Record<string, { label: string; variant: any; icon: any }> = {
      home: { label: "At Home", variant: "secondary", icon: Home },
      pickup_scheduled: { label: "Pickup Scheduled", variant: "outline", icon: Truck },
      in_transit: { label: "In Transit", variant: "outline", icon: Truck },
      stored: { label: "Stored", variant: "default", icon: Warehouse },
      returning: { label: "Returning", variant: "outline", icon: Truck },
    };

    const config = variants[status] || variants.home;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!box) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center">
        <div className="text-center">
          <Package className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-2xl font-semibold mb-2">Box Not Found</h2>
          <p className="text-muted-foreground mb-4">This box doesn't exist or you don't have access to it.</p>
          <Button onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  const locationIcon = box.location_status === "home" ? Home : Warehouse;
  const LocationIcon = locationIcon;

  // Determine the current stage of the box journey
  const getBoxStage = (): "at_home" | "pickup_scheduled" | "shipping_to_storage" | "in_storage" | "return_requested" | "returning" => {
    // Return requested but not shipped yet
    if (box.location_status === "stored" && box.return_requested_at && (!shipment || shipment.direction !== "to_customer")) {
      return "return_requested";
    }
    
    // Return shipment in progress
    if (shipment?.direction === "to_customer") {
      return "returning";
    }
    
    // Box is in storage and no return requested
    if (box.location_status === "stored" && !box.return_requested_at) {
      return "in_storage";
    }
    
    // Pickup scheduled to storage
    if (shipment?.type === "pickup" && shipment.status === "scheduled" && shipment.direction === "to_storage") {
      return "pickup_scheduled";
    }
    
    // Shipping to storage (has tracking number)
    if (box.tracking_number && box.location_status !== "stored" && (!shipment || shipment.direction === "to_storage")) {
      return "shipping_to_storage";
    }
    
    return "at_home";
  };

  return (
    <div className="min-h-screen bg-gradient-subtle">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => {
                  setEditedBox({
                    name: box.name,
                    description: box.description || "",
                    room: box.room,
                    box_number: box.box_number,
                  });
                  setShowEditBox(true);
                }}
              >
                <Edit2 className="h-4 w-4 mr-2" />
                Edit
              </Button>
              <Button variant="destructive" size="sm" onClick={handleDeleteBox}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Page Title */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Package className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold">{box.name}</h1>
          </div>
          <p className="text-muted-foreground">{box.box_number}</p>
        </div>
        {/* Edit Box Dialog */}
        <Dialog open={showEditBox} onOpenChange={setShowEditBox}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Box</DialogTitle>
              <DialogDescription>
                Update box information
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-box-number">Box Number *</Label>
                <Input
                  id="edit-box-number"
                  placeholder="e.g., ES-1001"
                  value={editedBox.box_number}
                  onChange={(e) =>
                    setEditedBox({ ...editedBox, box_number: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-name">Box Name *</Label>
                <Input
                  id="edit-name"
                  placeholder="e.g., Winter Clothes"
                  value={editedBox.name}
                  onChange={(e) =>
                    setEditedBox({ ...editedBox, name: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-description">Description</Label>
                <Textarea
                  id="edit-description"
                  placeholder="Describe what's in this box"
                  value={editedBox.description}
                  onChange={(e) =>
                    setEditedBox({ ...editedBox, description: e.target.value })
                  }
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-room">Room</Label>
                <Select
                  value={editedBox.room}
                  onValueChange={(value) =>
                    setEditedBox({ ...editedBox, room: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROOMS.map(room => (
                      <SelectItem key={room} value={room}>{room}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 pt-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowEditBox(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-gradient-primary"
                  onClick={handleEditBox}
                >
                  Save Changes
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <div className="flex flex-col lg:grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="order-2 lg:order-first lg:col-span-2 space-y-6">
            {/* Photos */}
            <Card className="shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold">Photos</h2>
                  <Dialog open={showPhotoOptions} onOpenChange={setShowPhotoOptions}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="bg-gradient-primary">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Photo
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add Photo</DialogTitle>
                        <DialogDescription>
                          Choose how to add a photo to this box
                        </DialogDescription>
                      </DialogHeader>
                      <div className="grid grid-cols-2 gap-4 py-4">
                        <div
                          className="flex flex-col items-center gap-3 p-6 border-2 border-dashed rounded-lg cursor-pointer hover:border-primary transition-smooth"
                          onClick={() => {
                            setShowPhotoOptions(false);
                            setShowCamera(true);
                          }}
                        >
                          <Camera className="h-8 w-8 text-primary" />
                          <span>Take Photo</span>
                        </div>
                        <Label
                          htmlFor="photo-upload"
                          className="flex flex-col items-center gap-3 p-6 border-2 border-dashed rounded-lg cursor-pointer hover:border-primary transition-smooth"
                        >
                          <Upload className="h-8 w-8 text-primary" />
                          <span>Upload Photo</span>
                          <Input
                            type="file"
                            accept="image/*"
                            onChange={handleFileUpload}
                            className="hidden"
                            id="photo-upload"
                          />
                        </Label>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>

                {/* Insurance reminder if label exists but no exterior photo */}
                {box.label_generated_at && !box.has_exterior_photo && (
                  <Alert className="mb-4">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Insurance Coverage Available</AlertTitle>
                    <AlertDescription>
                      Take an exterior photo of your sealed box to activate $100 shipping insurance coverage.
                    </AlertDescription>
                  </Alert>
                )}

                {/* Side-by-Side Photo Layout */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Exterior Photo Column */}
                  <div>
                    <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                      <Shield className="h-4 w-4 text-green-600" />
                      Exterior Photo
                      <Badge variant="outline" className="text-xs">Required for Insurance</Badge>
                    </h3>
                    {photos.filter(p => p.photo_type === 'exterior').length > 0 ? (
                      <div className="grid grid-cols-2 gap-3">
                        {photos.filter(p => p.photo_type === 'exterior').map((photo) => (
                          <div
                            key={photo.id}
                            className="relative aspect-square rounded-lg overflow-hidden bg-muted group"
                          >
                            <img
                              src={getPhotoUrl(photo)}
                              alt="Box exterior"
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => setLightboxPhoto(getPhotoUrl(photo))}
                              >
                                <ZoomIn className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDeletePhoto(photo.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setPhotoType('exterior');
                          setShowPhotoOptions(true);
                        }}
                        className="w-full aspect-square border-2 border-dashed border-muted-foreground/25 rounded-lg hover:border-primary/50 hover:bg-accent/50 transition-colors flex flex-col items-center justify-center gap-3 text-muted-foreground hover:text-foreground"
                      >
                        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                          <Camera className="w-8 h-8" />
                        </div>
                        <div className="text-center px-4">
                          <p className="font-medium">Add Exterior Photo</p>
                          <p className="text-xs mt-1">Include the box label or number ({box.box_number}) in your photo for easy identification</p>
                        </div>
                      </button>
                    )}
                  </div>

                  {/* Contents Photos Column */}
                  <div>
                    <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                      <Package className="h-4 w-4" />
                      Contents Photos
                    </h3>
                    {photos.filter(p => !p.photo_type || p.photo_type === 'interior').length > 0 ? (
                      <div className="grid grid-cols-2 gap-3">
                        {photos.filter(p => !p.photo_type || p.photo_type === 'interior').map((photo) => (
                          <div
                            key={photo.id}
                            className="relative aspect-square rounded-lg overflow-hidden bg-muted group"
                          >
                            <img
                              src={getPhotoUrl(photo)}
                              alt="Box contents"
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => setLightboxPhoto(getPhotoUrl(photo))}
                              >
                                <ZoomIn className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDeletePhoto(photo.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setPhotoType('interior');
                          setShowPhotoOptions(true);
                        }}
                        className="w-full aspect-square border-2 border-dashed border-muted-foreground/25 rounded-lg hover:border-primary/50 hover:bg-accent/50 transition-colors flex flex-col items-center justify-center gap-3 text-muted-foreground hover:text-foreground"
                      >
                        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                          <Camera className="w-8 h-8" />
                        </div>
                        <div className="text-center px-4">
                          <p className="font-medium">Add Contents Photo</p>
                          <p className="text-xs mt-1">Photo of items inside the box</p>
                        </div>
                      </button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Items */}
            <Card className="shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold">Items ({items.length})</h2>
                  <Dialog open={showAddItem} onOpenChange={setShowAddItem}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="bg-gradient-primary">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Item
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add New Item</DialogTitle>
                        <DialogDescription>
                          Add an item to {box.name}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label htmlFor="item-name">Item Name *</Label>
                          <Input
                            id="item-name"
                            placeholder="e.g., Winter Coat"
                            value={newItem.name}
                            onChange={(e) =>
                              setNewItem({ ...newItem, name: e.target.value })
                            }
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="quantity">Quantity</Label>
                            <Input
                              id="quantity"
                              type="number"
                              min="1"
                              value={newItem.quantity}
                              onChange={(e) =>
                                setNewItem({
                                  ...newItem,
                                  quantity: parseInt(e.target.value) || 1,
                                })
                              }
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="item-room">Room</Label>
                            <Select
                              value={newItem.room}
                              onValueChange={(value) =>
                                setNewItem({ ...newItem, room: value })
                              }
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {ROOMS.map(room => (
                                  <SelectItem key={room} value={room}>{room}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="notes">Notes (Optional)</Label>
                          <Input
                            id="notes"
                            placeholder="Additional details"
                            value={newItem.notes}
                            onChange={(e) =>
                              setNewItem({ ...newItem, notes: e.target.value })
                            }
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            className="flex-1"
                            onClick={() => setShowAddItem(false)}
                          >
                            Cancel
                          </Button>
                          <Button
                            className="flex-1 bg-gradient-primary"
                            onClick={handleAddItem}
                          >
                            Add Item
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>

                {items.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No items yet. Add items to catalog what's in this box.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {items.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-secondary/50 transition-smooth"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{item.name}</span>
                            {item.ai_generated && (
                              <Badge variant="secondary" className="text-xs">
                                AI
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            Qty: {item.quantity} · {item.room}
                            {item.notes && ` · ${item.notes}`}
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditItem(item.id)}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteItem(item.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Edit Item Dialog */}
            <Dialog open={showEditItem} onOpenChange={setShowEditItem}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Edit Item</DialogTitle>
                  <DialogDescription>
                    Update item details
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-item-name">Item Name *</Label>
                    <Input
                      id="edit-item-name"
                      placeholder="e.g., Winter Coat"
                      value={editedItem.name}
                      onChange={(e) =>
                        setEditedItem({ ...editedItem, name: e.target.value })
                      }
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-quantity">Quantity</Label>
                      <Input
                        id="edit-quantity"
                        type="number"
                        min="1"
                        value={editedItem.quantity}
                        onChange={(e) =>
                          setEditedItem({
                            ...editedItem,
                            quantity: parseInt(e.target.value) || 1,
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-item-room">Room</Label>
                      <Select
                        value={editedItem.room}
                        onValueChange={(value) =>
                          setEditedItem({ ...editedItem, room: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ROOMS.map(room => (
                            <SelectItem key={room} value={room}>{room}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-notes">Notes (Optional)</Label>
                    <Input
                      id="edit-notes"
                      placeholder="Additional details"
                      value={editedItem.notes}
                      onChange={(e) =>
                        setEditedItem({ ...editedItem, notes: e.target.value })
                      }
                    />
                  </div>
                  <div className="flex gap-2 pt-4">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setShowEditItem(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="flex-1 bg-gradient-primary"
                      onClick={handleSaveItem}
                    >
                      Save Changes
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Sidebar */}
          <div className="order-1 lg:order-last space-y-6">
            {/* Stage-based rendering of status cards */}
            {getBoxStage() === "pickup_scheduled" && shipment && (
              <PickupDetailsCard shipment={shipment} />
            )}

            {getBoxStage() === "shipping_to_storage" && (
              <ShippingToStorageCard 
                box={box} 
                onEmailLabel={handleEmailLabel}
              />
            )}

            {getBoxStage() === "in_storage" && (
              <>
                <InStorageCard box={box} />
                <RequestReturnCard 
                  boxId={box.id} 
                  boxName={box.name}
                  onReturnRequested={fetchBoxData}
                />
              </>
            )}

            {getBoxStage() === "return_requested" && shipment && (
              <>
                <Card className="shadow-sm">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="h-5 w-5 text-primary" />
                      <div className="font-semibold">Return Requested</div>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Your return request has been submitted. We'll process it and send you shipping details within 1-2 business days.
                    </p>
                    {box.return_requested_at && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Requested: {format(new Date(box.return_requested_at), "MMM d, yyyy 'at' h:mm a")}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </>
            )}

            {getBoxStage() === "returning" && shipment && (
              <ReturnShipmentCard 
                shipment={shipment}
                onEmailLabel={handleEmailLabel}
              />
            )}

            {/* Box Info - Always show */}
            <Card className="shadow-sm">
              <CardContent className="p-6 space-y-4">
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Location</div>
                  <div className="flex items-center gap-2">
                    <LocationIcon className="h-4 w-4 text-primary" />
                    <span className="font-medium">
                      {box.location_status === "home" ? "At Home" : 
                       box.location_status === "stored" ? "In Storage" : 
                       box.location_status === "in_transit" ? "In Transit" : "At Home"}
                    </span>
                  </div>
                </div>

                <div>
                  <div className="text-sm text-muted-foreground mb-1">Room</div>
                  <Badge variant="secondary">{box.room}</Badge>
                </div>

                <div>
                  <div className="text-sm text-muted-foreground mb-1">Total Items</div>
                  <div className="font-semibold">{items.length}</div>
                </div>

                <div>
                  <div className="text-sm text-muted-foreground mb-1">Created</div>
                  <div className="text-sm">
                    {new Date(box.created_at).toLocaleDateString()}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Photo Lightbox */}
      <Dialog open={!!lightboxPhoto} onOpenChange={() => setLightboxPhoto(null)}>
        <DialogContent className="max-w-4xl p-0">
          <div className="relative">
            <Button
              size="sm"
              variant="ghost"
              className="absolute top-2 right-2 z-10"
              onClick={() => setLightboxPhoto(null)}
            >
              <X className="h-4 w-4" />
            </Button>
            {lightboxPhoto && (
              <img
                src={lightboxPhoto}
                alt="Full size"
                className="w-full h-auto"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      <CameraCapture
        open={showCamera}
        onClose={() => setShowCamera(false)}
        onCapture={handleCameraCapture}
      />
    </div>
  );
};

export default BoxDetailConnected;
